import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Clock, Plus, Trash2, Save } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';

const DAY_KEYS = ['daySunday', 'dayMonday', 'dayTuesday', 'dayWednesday', 'dayThursday', 'dayFriday', 'daySaturday'];

function emptyDay(dayOfWeek) {
  return {
    day_of_week: dayOfWeek,
    is_closed: true,
    opening_time_1: null,
    closing_time_1: null,
    opening_time_2: null,
    closing_time_2: null
  };
}

function emptySpecial() {
  return { special_date: '', is_closed: true, opening_time_1: '', closing_time_1: '', opening_time_2: '', closing_time_2: '', notes: '' };
}

function emptyRange() {
  return { start_date: '', end_date: '', is_closed: true, opening_time_1: '', closing_time_1: '', opening_time_2: '', closing_time_2: '', notes: '' };
}

function timeToInput(t) {
  if (!t) return '';
  if (typeof t === 'string' && t.length >= 5) return t.slice(0, 5);
  return t;
}

export default function GymOpeningHours() {
  const { getAuthenticatedApi } = useAuth();
  const { t } = useTranslation();
  const [gymId, setGymId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [standard, setStandard] = useState(() => Array.from({ length: 7 }, (_, i) => emptyDay(i)));
  const [special, setSpecial] = useState([]);
  const [specialRanges, setSpecialRanges] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const api = getAuthenticatedApi();
      const gymsRes = await api.get('/gyms.php');
      const gym = gymsRes.data?.[0];
      if (!gym?.id) {
        toast.error('Palestra non trovata');
        return;
      }
      setGymId(gym.id);
      const hoursRes = await api.get(`/gym_opening_hours.php?action=get&gym_id=${gym.id}`);
      if (hoursRes.data?.success && hoursRes.data?.opening_hours) {
        const { standard: std, special: sp, special_ranges: sr } = hoursRes.data.opening_hours;
        if (std?.length) {
          const byDay = {};
          std.forEach((row) => { byDay[row.day_of_week] = { ...row, is_closed: !!row.is_closed }; });
          setStandard(Array.from({ length: 7 }, (_, i) => byDay[i] || emptyDay(i)));
        }
        if (Array.isArray(sp)) setSpecial(sp.map((s) => ({ ...s, special_date: s.special_date || '', notes: s.notes || '' })));
        if (Array.isArray(sr)) setSpecialRanges(sr.map((r) => ({ ...r, notes: r.notes || '' })));
      }
    } catch (err) {
      console.error(err);
      toast.error(t('gym.openingHours.saveError'));
    } finally {
      setLoading(false);
    }
  }, [getAuthenticatedApi, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateDay = (dayIndex, field, value) => {
    setStandard((prev) => {
      const next = [...prev];
      const row = { ...next[dayIndex] };
      if (field === 'is_closed') row.is_closed = value;
      else row[field] = value || null;
      next[dayIndex] = row;
      return next;
    });
  };

  const addSpecial = () => setSpecial((prev) => [...prev, emptySpecial()]);
  const removeSpecial = (i) => setSpecial((prev) => prev.filter((_, idx) => idx !== i));
  const updateSpecial = (i, field, value) => {
    setSpecial((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  };

  const addRange = () => setSpecialRanges((prev) => [...prev, emptyRange()]);
  const removeRange = (i) => setSpecialRanges((prev) => prev.filter((_, idx) => idx !== i));
  const updateRange = (i, field, value) => {
    setSpecialRanges((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  };

  const handleSave = async () => {
    if (!gymId) return;
    setSaving(true);
    try {
      const api = getAuthenticatedApi();
      const payload = {
        gym_id: gymId,
        standard: standard.map((s) => ({
          day_of_week: s.day_of_week,
          is_closed: s.is_closed,
          opening_time_1: s.opening_time_1 || null,
          closing_time_1: s.closing_time_1 || null,
          opening_time_2: s.opening_time_2 || null,
          closing_time_2: s.closing_time_2 || null
        })),
        special: special.filter((s) => s.special_date).map((s) => ({
          special_date: s.special_date,
          is_closed: s.is_closed,
          opening_time_1: s.opening_time_1 || null,
          closing_time_1: s.closing_time_1 || null,
          opening_time_2: s.opening_time_2 || null,
          closing_time_2: s.closing_time_2 || null,
          notes: s.notes || null
        })),
        special_ranges: specialRanges.filter((r) => r.start_date && r.end_date).map((r) => ({
          start_date: r.start_date,
          end_date: r.end_date,
          is_closed: r.is_closed,
          opening_time_1: r.opening_time_1 || null,
          closing_time_1: r.closing_time_1 || null,
          opening_time_2: r.opening_time_2 || null,
          closing_time_2: r.closing_time_2 || null,
          notes: r.notes || null
        }))
      };
      await api.post('/gym_opening_hours.php?action=save', payload);
      toast.success(t('gym.openingHours.saveSuccess'));
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || t('gym.openingHours.saveError');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Clock size={28} className="text-indigo-600 dark:text-indigo-400" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('gym.openingHours.title')}</h1>
          </div>
          <p className="text-gray-600 dark:text-white/65">{t('gym.openingHours.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 shrink-0"
        >
          <Save size={20} />
          {saving ? t('gym.openingHours.saving') : t('gym.openingHours.save')}
        </button>
      </div>

      {/* Settimana tipo - griglia a più colonne */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Settimana tipo</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {standard.map((row, idx) => (
            <DayRow
              key={idx}
              dayLabel={t(`gym.openingHours.${DAY_KEYS[idx]}`)}
              row={row}
              onChange={(field, value) => updateDay(idx, field, value)}
              t={t}
            />
          ))}
        </div>
      </section>

      {/* Date speciali + Intervalli affiancati su schermi grandi */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-10">
        {/* Date speciali singole */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{t('gym.openingHours.specialDates')}</h2>
          <p className="text-sm text-gray-500 dark:text-white/65 mb-4">{t('gym.openingHours.specialDatesHelp')}</p>
          <div className="space-y-3">
            {special.map((s, i) => (
              <div key={i} className="flex gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-white/10">
                <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-white/65 mb-1">{t('gym.openingHours.singleDate')}</label>
                    <input
                      type="date"
                      value={s.special_date}
                      onChange={(e) => updateSpecial(i, 'special_date', e.target.value)}
                      className="w-full rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                  </div>
                  <SpecialOrClosedRow row={s} onChange={(f, v) => updateSpecial(i, f, v)} t={t} />
                  <div className="sm:col-span-2">
                    <input
                      type="text"
                      placeholder={t('gym.openingHours.notesPlaceholder')}
                      value={s.notes || ''}
                      onChange={(e) => updateSpecial(i, 'notes', e.target.value)}
                      className="w-full rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <button type="button" onClick={() => removeSpecial(i)} className="p-2 h-fit text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded shrink-0" title={t('gym.openingHours.delete')}>
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addSpecial} className="mt-3 flex items-center gap-2 text-indigo-600 dark:text-indigo-400 text-sm font-medium">
            <Plus size={16} /> {t('gym.openingHours.addSingleDate')}
          </button>
        </section>

        {/* Intervalli */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Intervalli (es. ferie)</h2>
          <p className="text-sm text-gray-500 dark:text-white/65 mb-4">Periodi con stessi orari o chiusura (es. settimana di ferie).</p>
          <div className="space-y-3">
            {specialRanges.map((r, i) => (
              <div key={i} className="flex gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-white/10">
                <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-white/65 mb-1">{t('gym.openingHours.startDate')}</label>
                    <input
                      type="date"
                      value={r.start_date}
                      onChange={(e) => updateRange(i, 'start_date', e.target.value)}
                      className="w-full rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-white/65 mb-1">{t('gym.openingHours.endDate')}</label>
                    <input
                      type="date"
                      value={r.end_date}
                      onChange={(e) => updateRange(i, 'end_date', e.target.value)}
                      className="w-full rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                  </div>
                  <SpecialOrClosedRow row={r} onChange={(f, v) => updateRange(i, f, v)} t={t} />
                  <div className="sm:col-span-2">
                    <input
                      type="text"
                      placeholder={t('gym.openingHours.notesPlaceholder')}
                      value={r.notes || ''}
                      onChange={(e) => updateRange(i, 'notes', e.target.value)}
                      className="w-full rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <button type="button" onClick={() => removeRange(i)} className="p-2 h-fit text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded shrink-0" title={t('gym.openingHours.delete')}>
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addRange} className="mt-3 flex items-center gap-2 text-indigo-600 dark:text-indigo-400 text-sm font-medium">
            <Plus size={16} /> {t('gym.openingHours.addRange')}
          </button>
        </section>
      </div>
    </div>
  );
}

function isEmptyTime(t) {
  return t == null || t === '';
}

function DayRow({ dayLabel, row, onChange, t }) {
  const is24h = !row.is_closed && isEmptyTime(row.opening_time_1) && isEmptyTime(row.closing_time_1);
  const [showTimeFields, setShowTimeFields] = React.useState(!is24h);
  const hasSecond = row.opening_time_2 != null && row.opening_time_2 !== '' || row.closing_time_2 != null && row.closing_time_2 !== '';
  return (
    <div className="p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-lg shadow-sm flex flex-col gap-3">
      <div className="font-semibold text-gray-900 dark:text-white border-b border-gray-100 dark:border-white/10 pb-2">
        {dayLabel}
      </div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!row.is_closed}
          onChange={(e) => onChange('is_closed', !e.target.checked)}
          className="rounded border-gray-300 text-indigo-600"
        />
        <span className="text-sm text-gray-700 dark:text-white/85">{t('gym.openingHours.open')}</span>
      </label>
      {!row.is_closed && (
        <div className="space-y-2">
          {is24h && !showTimeFields ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">{t('gym.openingHours.open24h')}</span>
              <button type="button" onClick={() => setShowTimeFields(true)} className="text-xs text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 underline">{t('gym.openingHours.setHours')}</button>
            </div>
          ) : (
            <>
              {is24h && <p className="text-xs text-gray-500 dark:text-white/65">{t('gym.openingHours.open24hSetHoursHint')}</p>}
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="time"
                  value={timeToInput(row.opening_time_1)}
                  onChange={(e) => onChange('opening_time_1', e.target.value)}
                  className="rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm w-24"
                />
                <span className="text-gray-500 text-sm">-</span>
                <input
                  type="time"
                  value={timeToInput(row.closing_time_1)}
                  onChange={(e) => onChange('closing_time_1', e.target.value)}
                  className="rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm w-24"
                />
              </div>
              {hasSecond ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <input type="time" value={timeToInput(row.opening_time_2)} onChange={(e) => onChange('opening_time_2', e.target.value)} className="rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm w-24" />
                    <span className="text-gray-500 text-sm">-</span>
                    <input type="time" value={timeToInput(row.closing_time_2)} onChange={(e) => onChange('closing_time_2', e.target.value)} className="rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm w-24" />
                  </div>
                  <button type="button" onClick={() => { onChange('opening_time_2', null); onChange('closing_time_2', null); }} className="text-xs text-gray-500 hover:text-red-600">{t('gym.openingHours.removeSecondInterval')}</button>
                </div>
              ) : (
                <button type="button" onClick={() => { onChange('opening_time_2', '16:00'); onChange('closing_time_2', '21:00'); }} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">{t('gym.openingHours.addSecondInterval')}</button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SpecialOrClosedRow({ row, onChange, t }) {
  const is24h = !row.is_closed && isEmptyTime(row.opening_time_1) && isEmptyTime(row.closing_time_1);
  const [showTimeFields, setShowTimeFields] = React.useState(!is24h);
  return (
    <div className="sm:col-span-2 space-y-3">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!!row.is_closed}
          onChange={(e) => onChange('is_closed', e.target.checked)}
          className="rounded border-gray-300 text-indigo-600"
        />
        <span className="text-sm text-gray-700 dark:text-white/85">{t('gym.openingHours.closedAllDay')}</span>
      </label>
      {!row.is_closed && (
        <div className="space-y-2 pl-6 border-l-2 border-gray-200 dark:border-white/10">
          {is24h && !showTimeFields ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">{t('gym.openingHours.open24h')}</span>
              <button type="button" onClick={() => setShowTimeFields(true)} className="text-xs text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 underline">{t('gym.openingHours.setHours')}</button>
            </div>
          ) : (
            <>
              {is24h && <p className="text-xs text-gray-500 dark:text-white/65">{t('gym.openingHours.open24hSetHoursHint')}</p>}
              <p className="text-xs text-gray-500 dark:text-white/65">{t('gym.openingHours.openWithHours')}</p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-white/65 w-12">{t('gym.openingHours.from')}</span>
                <input type="time" value={timeToInput(row.opening_time_1)} onChange={(e) => onChange('opening_time_1', e.target.value)} className="rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm w-28" />
                <span className="text-xs text-gray-500 dark:text-white/65">{t('gym.openingHours.to')}</span>
                <input type="time" value={timeToInput(row.closing_time_1)} onChange={(e) => onChange('closing_time_1', e.target.value)} className="rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm w-28" />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-white/65 w-12">{t('gym.openingHours.from')}</span>
                <input type="time" value={timeToInput(row.opening_time_2)} onChange={(e) => onChange('opening_time_2', e.target.value)} className="rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm w-28" />
                <span className="text-xs text-gray-500 dark:text-white/65">{t('gym.openingHours.to')}</span>
                <input type="time" value={timeToInput(row.closing_time_2)} onChange={(e) => onChange('closing_time_2', e.target.value)} className="rounded border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm w-28" />
                <span className="text-xs text-gray-500 dark:text-white/65">(2°)</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
